import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * C5: the one button component. Renders <Link> for `to`, <a> for `href`
 * (external links open in a new tab with rel noopener), otherwise <button>.
 *
 *   <Button to={SITE.booking.chooserPath}>Book Online</Button>
 *   <Button to="/contact-us/" variant="outline">Contact us</Button>
 *   <Button type="submit" busy={submitting}>Send</Button>
 */
export type ButtonVariant = 'solid' | 'outline' | 'outline-navy' | 'outline-light' | 'white' | 'gold' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Full-width button (forms, mobile CTAs). */
  block?: boolean;
  /** Busy state: aria-busy, pointer-events off, spinner before the label. */
  busy?: boolean;
  /** Disabled state rendered as aria-disabled so the element stays focusable/readable. */
  disabled?: boolean;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  className?: string;
  children: ReactNode;
}

type AnchorExtras = Omit<ComponentPropsWithoutRef<'a'>, keyof ButtonBaseProps | 'href'>;
type ButtonExtras = Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonBaseProps | 'type'>;

export type ButtonProps = ButtonBaseProps & {
  /** Internal route: renders react-router <Link>. */
  to?: string;
  /** Plain URL: renders <a>. http(s) URLs (or `external`) open in a new tab. */
  href?: string;
  external?: boolean;
  type?: 'button' | 'submit' | 'reset';
} & AnchorExtras &
  ButtonExtras;

export function buttonClassName({
  variant = 'solid',
  size = 'md',
  block = false,
  className = '',
}: Pick<ButtonBaseProps, 'variant' | 'size' | 'block' | 'className'>): string {
  return [
    've-btn',
    variant !== 'solid' ? `ve-btn--${variant}` : '',
    size !== 'md' ? `ve-btn--${size}` : '',
    block ? 've-btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'solid',
    size = 'md',
    block = false,
    busy = false,
    disabled = false,
    iconBefore,
    iconAfter,
    className = '',
    children,
    to,
    href,
    external,
    type = 'button',
    ...rest
  },
  ref,
) {
  const cls = buttonClassName({ variant, size, block, className });
  const inner = (
    <>
      {busy ? <span className="ve-btn-spinner" aria-hidden="true" /> : iconBefore}
      <span>{children}</span>
      {iconAfter}
    </>
  );
  const stateProps = {
    'aria-busy': busy || undefined,
    'aria-disabled': disabled || busy || undefined,
  };

  if (to) {
    return (
      <Link
        to={to}
        className={cls}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...stateProps}
        {...(rest as ComponentPropsWithoutRef<'a'>)}
      >
        {inner}
      </Link>
    );
  }

  if (href) {
    const isExternal = external ?? /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        className={cls}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...stateProps}
        {...(rest as ComponentPropsWithoutRef<'a'>)}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={cls}
      ref={ref as React.Ref<HTMLButtonElement>}
      disabled={disabled && !busy ? true : undefined}
      {...stateProps}
      {...(rest as ComponentPropsWithoutRef<'button'>)}
    >
      {inner}
    </button>
  );
});

export default Button;
